import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Divider, Grid, makeStyles, TextField, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AppContext from "context/AppContext";
import { useTranslation } from "react-i18next";
import { getConnectorMappingFields } from 'config/shipperPackageFormConfig';

const useStyles = makeStyles(() => ({
    root: {},
    input: {
        '&>.MuiFormHelperText-root.Mui-error': {
            marginLeft: '4px',
            marginRight: '4px'
        }
    },
    errorAlert: {
        marginBottom: '20px'
    }
}));

const DISABLED_FOR_PARCELSHOPS = ['receiver_company', 'receiver_street', 'receiver_house_number', 'receiver_zip', 'receiver_city', 'receiver_state_code', 'count_of_packages'];

const PackageStep = ({ shipperId, packages, handlePackagesStateChange, componentKey, ...rest }) => {
    const classes = useStyles();
    const { t } = useTranslation('connector');
    const { appValues: AppValues } = useContext(AppContext);
    const [shipper, setShipper] = useState(AppValues.shippers.find(s => s.id == shipperId));

    const editableFields = useMemo(
        () => (shipper ? getConnectorMappingFields(shipper.shortcut) : []),
        [shipper]
    );

    useEffect(() => {
        setShipper(AppValues.shippers.find(s => s.id == shipperId));
    }, [AppValues, shipperId]);

    const isInputError = (errors, fieldname) => {
        return errors[fieldname] && errors[fieldname].length > 0;
    };

    const getErrorMessage = (errors, fieldname) => {
        if (typeof errors[fieldname] === 'string') {
            return errors[fieldname];
        }
        return errors[fieldname] && errors[fieldname].length > 0 ? errors[fieldname].join(', ') : '';
    };

    const getOtherErrors = (pErrors, getArray = false) => {
        let errors = [];
        for (const key in pErrors) {
            if (key.includes('receiver') || ['insurance', 'cod_reference', 'cod_price', 'cod_currency_code', 'count_of_packages', 'weight'].includes(key) || pErrors[key].length === 0 ) {
                continue;
            }
            if (typeof pErrors[key] === 'object') {
                let tmpRes = getOtherErrors(pErrors[key], true);
                if (tmpRes.length !== 0)
                    errors = errors.concat(key + " : " + tmpRes.join(','));
            } else {
                errors.push(pErrors[key]);
            }
        }
        if (getArray) {
            return errors;
        }
        return errors.length ? errors.join(', ') : null;
    };

    const getFieldValue = (pack, fieldName) => {
        if (fieldName.startsWith('others_')) {
            const othersKey = fieldName.substring(7);
            return pack.package.others?.[othersKey] ?? '';
        }
        return pack.package[fieldName] ?? (fieldName === 'count_of_packages' ? 1 : '');
    };

    const onChange = (e, index) => {
        const packs = packages.packages;
        const { name, value } = e.target;
        if (name.startsWith('others_')) {
            const othersKey = name.substring(7);
            if (!packs[index].package.others) {
                packs[index].package.others = {};
            }
            packs[index].package.others[othersKey] = value;
        } else {
            packs[index].package[name] = value;
        }
        handlePackagesStateChange(shipperId, [...packs]);
    };

    return (
        <React.Fragment>
            <Typography variant={'h3'}>
                {shipper?.shortcut}
            </Typography>
            <Card>
                <CardContent key={`${componentKey}-card-content`}>
                    {
                        !packages.imported && packages.errorMessage &&
                        <Alert severity="error" className={classes.errorAlert}>{packages.errorMessage}</Alert>
                    }
                    {
                        !packages.imported && packages.packages.map((pack, index) =>
                            <Grid container spacing={2} key={index}>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {index + 1}. {pack.package['reference_number']}
                                    </Typography>
                                </Grid>
                                {
                                    editableFields.map((field, i) =>
                                        <Grid item xs={3} key={i}>
                                            <TextField
                                                fullWidth
                                                className={classes.input}
                                                label={t(`label.${field}`)}
                                                value={getFieldValue(pack, field)}
                                                variant="outlined"
                                                size="small"
                                                onChange={(e) => onChange(e, index)}
                                                name={field}
                                                error={isInputError(pack.errors, field)}
                                                helperText={getErrorMessage(pack.errors, field)}
                                                disabled={pack.package.parcelshop && DISABLED_FOR_PARCELSHOPS.includes(field)}
                                                {...(field === 'count_of_packages' && { type: 'number', inputProps: { inputMode: 'numeric', pattern: '[0-9]*' } })}
                                            />
                                        </Grid>
                                    )
                                }
                                {
                                    getOtherErrors(pack.errors) &&
                                    <Grid item xs={12}>
                                        <Alert variant="filled" severity="error">
                                            {getOtherErrors(pack.errors) }
                                        </Alert>
                                    </Grid>
                                }
                                {
                                    index !== packages.packages.length-1 &&
                                    <Grid item xs={12}>
                                        <Divider/>
                                    </Grid>
                                }
                            </Grid>
                        )
                    }
                    {
                        packages.imported &&
                            <Alert severity="success">{t('messages.successful_import_to_neoship')}</Alert>
                    }
                </CardContent>
            </Card>
        </React.Fragment>
    )
}

export default PackageStep;